import type { ReactNode } from 'react';

import { FileText } from 'lucide-react';

import { DialogContent } from '@/components/ui/dialog';

import { DocumentFiguresCard } from '../components/document-figures';
import { InvoiceCreatePdfPicker } from '../components/invoice-create-pdf-picker';
import { InvoicePdfPanel } from '../components/invoice-pdf-panel';
import type { InvoiceRow } from '../types';

// Sell-side documents are rendered by us at issue time, never uploaded — the
// create pane says so instead of offering a picker.
function GeneratedDocumentNote(): ReactNode {
  return (
    <section aria-labelledby="invoice-generated-pdf-heading" className="flex flex-col gap-3">
      <h3 id="invoice-generated-pdf-heading" className="text-sm font-semibold">
        Invoice PDF
      </h3>
      <div
        className="bg-muted/30 text-muted-foreground flex flex-col items-center justify-center gap-2 rounded-md border border-dashed p-6 text-center text-sm"
        data-testid="invoice-generated-pdf-note"
      >
        <FileText className="size-8" aria-hidden />
        <p>
          This document is generated by Foundry. You can preview it during the review step and open
          the final PDF once it is issued.
        </p>
      </div>
    </section>
  );
}

// The right-pane PDF surface for an invoice dialog. The buy side manages the
// received supplier invoice: pick it at create time (it gates the
// raise-for-approval step — say so where the operator attaches it instead of
// in a rejection toast later) and replace it in edit mode. The sell side never
// uploads: we generate the document, so create shows a note and edit shows any
// existing attachment read-only (legacy uploads stay visible, not editable).
export function InvoiceDialogRightPane({
  existingInvoice,
  file,
  onFileChange,
  side,
}: Readonly<{
  existingInvoice: InvoiceRow | null;
  file: File | null;
  onFileChange: (file: File | null) => void;
  side?: 'buy' | 'sell';
}>): ReactNode {
  if (side === 'sell') {
    // While editing, lead with the figures the generated document resolves
    // today — the edit view never types onto the document, so the operator
    // needs to see what a save will regenerate.
    return existingInvoice !== null ? (
      <>
        <DocumentFiguresCard invoiceId={existingInvoice.id} />
        <InvoicePdfPanel invoiceId={existingInvoice.id} readOnly />
      </>
    ) : (
      <GeneratedDocumentNote />
    );
  }
  return (
    <>
      {existingInvoice !== null ? (
        <InvoicePdfPanel invoiceId={existingInvoice.id} />
      ) : (
        <InvoiceCreatePdfPicker file={file} onFileChange={onFileChange} />
      )}
      {side === 'buy' && (
        <p className="text-muted-foreground mt-2 text-xs" data-testid="invoice-pdf-raise-note">
          The received supplier PDF is required before this invoice can be raised for approval.
        </p>
      )}
    </>
  );
}

// DialogContent wrapper for the two-pane trade invoice dialogs whose form
// spans the whole modal: header + fields on the left, the PDF pane on the
// right, and a pinned full-width action footer at the bottom. The hosting
// `<form>` is the flex column so the body scrolls and the footer stays put.
export function InvoiceDialogContent({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-2xl lg:max-w-5xl">
      {children}
    </DialogContent>
  );
}

// The scrolling two-pane body + a bottom-pinned, right-aligned footer divided
// by a full-width rule. Rendered inside the dialog's `<form>` so the submit
// button in `footer` keeps its native form association. The body is a single
// scroll context — the wheel works wherever the cursor sits, including over
// the PDF pane, which stays pinned via sticky while the form scrolls past.
export function InvoiceDialogFormShell({
  rightPane,
  footer,
  children,
}: Readonly<{ rightPane: ReactNode; footer: ReactNode; children: ReactNode }>) {
  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto lg:flex-row">
        <div className="flex flex-1 flex-col gap-4 lg:pr-1">{children}</div>
        <div className="flex flex-col lg:sticky lg:top-0 lg:w-[24rem] lg:self-start lg:border-l lg:pl-4">
          {rightPane}
        </div>
      </div>
      <div className="mt-4 border-t pt-4">{footer}</div>
    </>
  );
}

export interface InvoiceDialogShellProps {
  // The right-pane Invoice-PDF surface (edit panel or create picker). When
  // omitted the dialog renders single-pane.
  readonly rightPane?: ReactNode;
  readonly singlePaneClassName: string;
  readonly children: ReactNode;
}

// Wraps the invoice create/edit form in a two-pane layout when a PDF surface
// applies. The form column scrolls; the PDF pane sits to its right on large
// screens and stacks below on phones.
export function InvoiceDialogShell({
  rightPane,
  singlePaneClassName,
  children,
}: Readonly<InvoiceDialogShellProps>) {
  if (rightPane === undefined) {
    return <DialogContent className={singlePaneClassName}>{children}</DialogContent>;
  }
  return (
    <DialogContent className="flex max-h-[90vh] flex-col gap-4 overflow-hidden sm:max-w-2xl lg:max-w-5xl">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto lg:flex-row">
        <div className="flex flex-1 flex-col gap-4 lg:pr-1">{children}</div>
        <div className="flex flex-col lg:sticky lg:top-0 lg:w-[24rem] lg:self-start lg:border-l lg:pl-4">
          {rightPane}
        </div>
      </div>
    </DialogContent>
  );
}
